/**
 * SwipeActionBar — Like / Skip / Dislike three-button action bar.
 *
 * Keyboard shortcuts:
 *   ArrowRight or l/L → Like
 *   ArrowLeft  or d/D → Dislike
 *   ArrowDown  or s/S → Skip
 *
 * Keyboard handler is not active when focus is in input/select/textarea/button.
 * Handler is inactive when disabled=true. Cleans up on unmount.
 */

import { useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SwipeActionBarProps {
  onLike: () => void;
  onDislike: () => void;
  onSkip: () => void;
  disabled?: boolean;
}

// ─── Icons (inline SVG to avoid external deps) ───────────────────────────────

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" aria-hidden="true">
      <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-6 h-6" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ForwardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SwipeActionBar({ onLike, onDislike, onSkip, disabled = false }: SwipeActionBarProps) {
  useEffect(() => {
    if (disabled) return;

    function handleKeyDown(event: KeyboardEvent): void {
      // Do not capture keystrokes when focus is in a form field
      const target = event.target as Element | null;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'BUTTON' ||
        (target as HTMLElement).isContentEditable
      )) {
        return;
      }

      const key = event.key;

      if (key === 'ArrowRight' || key === 'l' || key === 'L') {
        event.preventDefault();
        onLike();
      } else if (key === 'ArrowLeft' || key === 'd' || key === 'D') {
        event.preventDefault();
        onDislike();
      } else if (key === 'ArrowDown' || key === 's' || key === 'S') {
        event.preventDefault();
        onSkip();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled, onLike, onDislike, onSkip]);

  return (
    <div className="flex items-center justify-center gap-6 py-4">
      {/* Dislike */}
      <button
        onClick={onDislike}
        disabled={disabled}
        aria-label="Dislike — press D or ←"
        className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-800 text-red-400 border border-red-400/40 shadow-lg hover:bg-red-900/30 hover:border-red-400/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <XIcon />
      </button>

      {/* Skip */}
      <button
        onClick={onSkip}
        disabled={disabled}
        aria-label="Skip — press S or ↓"
        className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 text-gray-400 border border-gray-600/50 shadow hover:bg-gray-700 hover:border-gray-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ForwardIcon />
      </button>

      {/* Like */}
      <button
        onClick={onLike}
        disabled={disabled}
        aria-label="Like — press L or →"
        className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-800 text-green-400 border border-green-400/40 shadow-lg hover:bg-green-900/30 hover:border-green-400/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <HeartIcon />
      </button>
    </div>
  );
}
